/**
 * Watering-window capacity fitting.
 *
 * The planner can produce more total runtime than the tenant's window holds
 * (many zones, short window). Left unchecked, the executor's sequence would
 * run past the "finish by" time. This module fits the day's plans into the
 * window: fixed-schedule zones first (an explicit user program), then smart
 * zones by soil stress. A zone that is merely CROWDED OUT — it would fit whole
 * in the window on its own, but higher-priority zones ahead of it have used the
 * time — is deferred ENTIRELY (a WINDOW_FULL skip), never clipped to fill the
 * leftover minutes. A partial watering is worse than a full watering a day
 * later, so the whole zone waits; its deficit carries forward and leads
 * tomorrow's priority. The one exception is a zone whose own full sequence is
 * longer than the entire window: that zone can never fit whole, so deferring it
 * would starve it forever — it is truncated to the window so it still waters.
 *
 * Capacity is compared against the sequence's true wall-clock duration. The
 * executor realizes cycle-and-soak as real idle soaks (one pulse per zone per
 * tick), so demand is valve-open minutes PLUS the soak gaps between rounds —
 * see {@link sequenceDurationMinutes}. The soak gaps are shared: all zones in a
 * round soak together, so the gap count is driven by the accepted set's
 * largest cycle count, not summed per zone.
 */
import { allowedDepletionIn } from './soil.js';
/** Minutes between the window's start and end ("HH:MM" each). A window that
 * wraps past midnight (end <= start) spans the gap plus the next morning. */
export function windowMinutes(window) {
    const span = hhmmToMinutes(window.latestEnd) - hhmmToMinutes(window.earliestStart);
    return span > 0 ? span : span + 24 * 60;
}
/** Minutes since local midnight for an "HH:MM" time (0 when unparsable). */
export function hhmmToMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}
/**
 * Estimated wall-clock minutes to run a day's plans under the executor's
 * one-pulse-per-tick cycle-and-soak. Each round runs one pulse of every zone
 * still cycling — valve-open minutes add up because Rachio runs the batch's
 * zones sequentially — then all soak together before the next round. The soak
 * gaps are real idle time. `tickLatencyMin` pads each round for the executor
 * only advancing on its fixed cadence (pass its interval to stay conservative).
 */
export function sequenceDurationMinutes(plans, soakMinutes, tickLatencyMin = 0) {
    const watering = plans.filter((p) => p.shouldWater && p.cycles.length > 0);
    if (watering.length === 0)
        return 0;
    const valveMinutes = watering.reduce((sum, p) => sum + p.totalRuntimeMinutes, 0);
    const maxCycles = Math.max(...watering.map((p) => p.cycles.length));
    const soakGaps = Math.max(0, maxCycles - 1);
    return valveMinutes + soakGaps * (soakMinutes + tickLatencyMin);
}
/** Don't bother truncating below this — a token sprinkle helps nothing. */
const MIN_USEFUL_RUN_MINUTES = 5;
const round = (n, dp = 2) => {
    const f = 10 ** dp;
    return Math.round(n * f) / f;
};
/**
 * Fit the day's plans into the watering window. Returns the final plan per
 * zone id — a zone crowded out by higher-priority zones is deferred whole, and
 * only a zone longer than the whole window on its own is truncated.
 */
export function fitPlansToWindow(planned, config) {
    const result = new Map(planned.map((p) => [p.zone.id, p.plan]));
    const capacity = windowMinutes(config.window);
    const watering = planned.filter((p) => p.plan.shouldWater);
    const soak = config.soakMinutes;
    if (capacity <= 0 || sequenceDurationMinutes(watering.map((p) => p.plan), soak) <= capacity)
        return result;
    const isFixed = (p) => (p.zone.scheduleMode ?? 'SMART') === 'FIXED';
    /** How overdue a smart zone is: deficit relative to its watering threshold. */
    const stress = (p) => {
        const threshold = allowedDepletionIn(p.zone);
        if (p.deficitIn !== undefined && threshold > 0)
            return p.deficitIn / threshold;
        return p.plan.netDepthIn;
    };
    const ordered = [...watering].sort((a, b) => {
        if (isFixed(a) !== isFixed(b))
            return isFixed(a) ? -1 : 1;
        if (isFixed(a))
            return a.zone.zoneNumber - b.zone.zoneNumber;
        return stress(b) - stress(a);
    });
    // Admit zones in priority order, tracking the accepted set's valve minutes and
    // largest cycle count (which drives the shared soak-gap count).
    let acceptedValve = 0;
    let acceptedMaxCycles = 0;
    for (const p of ordered) {
        const prospectiveMaxCycles = Math.max(acceptedMaxCycles, p.plan.cycles.length);
        const soakGaps = Math.max(0, prospectiveMaxCycles - 1);
        const prospectiveTotal = acceptedValve + p.plan.totalRuntimeMinutes + soakGaps * soak;
        if (prospectiveTotal <= capacity) {
            // Fits whole alongside what's accepted — leave the original plan in place.
            acceptedValve += p.plan.totalRuntimeMinutes;
            acceptedMaxCycles = prospectiveMaxCycles;
            continue;
        }
        // It doesn't fit whole right now. If its OWN full sequence would fit an empty
        // window, it is merely crowded out by the zones ahead of it — defer the whole
        // zone rather than clip it, so it waters in full on a less-full day. Only a
        // zone too long for the entire window on its own is truncated (deferring it
        // forever would starve it); it still takes the leftover time.
        const fitsWholeAlone = sequenceDurationMinutes([p.plan], soak) <= capacity;
        if (fitsWholeAlone) {
            result.set(p.zone.id, deferPlan(p.plan));
            continue;
        }
        const truncated = fitZoneToWindow(p.zone, p.plan, acceptedValve, acceptedMaxCycles, capacity, soak);
        if (truncated) {
            result.set(p.zone.id, truncated);
            acceptedValve += truncated.totalRuntimeMinutes;
            acceptedMaxCycles = Math.max(acceptedMaxCycles, truncated.cycles.length);
        }
        else {
            result.set(p.zone.id, deferPlan(p.plan));
        }
    }
    return result;
}
/**
 * Admit as much of `plan` as the remaining wall-clock room allows, given what's
 * already accepted. Returns a truncated plan (depths re-derived) when part fits,
 * or undefined to defer. Only used for a zone whose own full sequence exceeds
 * the entire window — a crowded-out zone is deferred whole by {@link
 * fitPlansToWindow} before this is reached. Each candidate cycle is charged its
 * run time plus, when it raises the accepted set's cycle count, the soak gap
 * that new round introduces.
 */
function fitZoneToWindow(zone, plan, acceptedValve, acceptedMaxCycles, capacity, soakMinutes) {
    const cycles = [];
    let valve = 0;
    let truncated = false;
    for (const cycle of plan.cycles) {
        const gaps = Math.max(0, Math.max(acceptedMaxCycles, cycles.length + 1) - 1);
        const room = capacity - acceptedValve - gaps * soakMinutes - valve;
        if (room <= 0) {
            truncated = true;
            break;
        }
        const runMinutes = Math.min(cycle.runMinutes, room);
        cycles.push({ runMinutes: round(runMinutes, 1), soakMinutes: cycle.soakMinutes });
        valve += runMinutes;
        if (runMinutes < cycle.runMinutes) {
            truncated = true;
            break;
        }
    }
    if (cycles.length === 0)
        return undefined;
    if (!truncated)
        return plan; // everything fit — hand back the original plan
    const last = cycles[cycles.length - 1];
    if (last)
        last.soakMinutes = 0;
    const runtime = cycles.reduce((sum, c) => sum + c.runMinutes, 0);
    if (runtime < MIN_USEFUL_RUN_MINUTES)
        return undefined; // token sliver — defer instead
    const grossApplied = (runtime / 60) * zone.precipRateInPerHr;
    const netApplied = grossApplied * zone.efficiency;
    return {
        ...plan,
        totalRuntimeMinutes: round(runtime, 1),
        grossDepthIn: round(grossApplied),
        netDepthIn: round(netApplied),
        cycles,
        explanation: `${plan.explanation} Truncated to ${round(runtime, 1)} min — the zone needs more than the ` +
            `whole watering window; the rest carries to tomorrow.`,
    };
}
function deferPlan(plan) {
    return {
        ...plan,
        shouldWater: false,
        netDepthIn: 0,
        grossDepthIn: 0,
        totalRuntimeMinutes: 0,
        cycles: [],
        skips: [...plan.skips, 'WINDOW_FULL'],
        explanation: 'Deferred — the watering window is full; needier zones used the available time. ' +
            'The deficit carries to tomorrow.',
    };
}
//# sourceMappingURL=capacity.js.map